import React from "react"

import type { ReactConsumer } from "@/lib/consume"

/* If you get an error in your consumer call that looks like:

  Type 'ProvidedProps<(type), (provideTo), undefined>' is missing the following properties from type 'Readonly<(props)>': (list of other props)

  You most likely have a type mismatch between the type of the provided prop on the component, and the type that results from calling your hook.

  A common error is making the provided prop an optional type. It should be a mandatory type, ie. no "?" at the end of the type name.

*/

export const consumeHook = function <
  ProvideTo extends string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Hook extends (...args: any[]) => any
>({
  getHookArguments,
  hook,
  provideTo,
}: {
  getHookArguments: () => Parameters<Hook>
  hook: Hook
  provideTo: ProvideTo
}) {
  return function <
    NestedComponentProps extends ReactConsumer.ProvidedProps<
      ReturnType<Hook>,
      ProvideTo
    >
  >(Component: React.ComponentType<NestedComponentProps>) {
    return function (
      externalProps: ReactConsumer.ExternalProps<
        NestedComponentProps,
        ProvideTo
      >
    ) {
      const hookArguments = getHookArguments()
      const hookResult = hook(...hookArguments)

      const nestedComponentProps = {
        ...externalProps,
        [provideTo]: hookResult,
      } as NestedComponentProps

      return <Component {...nestedComponentProps} />
    }
  }
}
